import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  opens: { title: "Who opened", subtitle: "Unique opens for this campaign" },
  clicks: { title: "Who clicked", subtitle: "All link clicks recorded for this campaign" },
  bounces: { title: "Who bounced", subtitle: "Delivery failures for this campaign" },
  unsubscribes: { title: "Who unsubscribed", subtitle: "Contacts who opted out from this campaign" },
};

export default function CampaignEngagementPage() {
  const { id, kind } = useParams();
  const meta = LABELS[kind] || { title: "Engagement", subtitle: "" };
  const [campaignName, setCampaignName] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetcher = useCallback(
    (page, limit) => api.get(`/campaigns/${id}/engagement/${kind}`, { params: { page, limit } }),
    [id, kind],
  );

  const {
    items: rows,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
  } = usePaginatedList(fetcher, [id, kind]);

  useEffect(() => {
    if (!id || !kind) return;
    api.get(`/campaigns/${id}/engagement/${kind}`, { params: { page: 1, limit: 1 } })
      .then((r) => setCampaignName(r.data.campaign_name || ""))
      .catch(() => setCampaignName(""));
  }, [id, kind]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/campaigns/${id}/engagement/${kind}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mailwave-${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const showUrl = kind === "clicks";
  const showCount = kind === "opens";
  const showBounceReason = kind === "bounces";

  return (
    <div data-testid={`campaign-engagement-${kind}`}>
      <PageHeader
        title={meta.title}
        subtitle={campaignName ? `${meta.subtitle} — ${campaignName}` : meta.subtitle}
        action={
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={exporting || total === 0}
            className="rounded-sm font-semibold"
            data-testid="engagement-export-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-6">
        <div className="flex flex-wrap gap-4 text-sm">
          <Link to={`/campaigns/${id}/analytics`} className="inline-flex items-center text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
            <ArrowLeft className="h-4 w-4 mr-1" /> Campaign analytics
          </Link>
          <Link to="/campaigns" className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 underline underline-offset-2">
            All campaigns
          </Link>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center text-sm text-neutral-500 dark:text-neutral-400">
            No {kind} recorded for this campaign yet.
          </div>
        ) : (
          <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex justify-between items-center">
              <span className="label-overline">{total.toLocaleString()} contact{total !== 1 ? "s" : ""}</span>
            </div>
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                  <th className="text-left px-6 py-3 label-overline">Email</th>
                  <th className="text-left px-6 py-3 label-overline">Name</th>
                  <th className="text-left px-6 py-3 label-overline">Tags</th>
                  {showUrl && <th className="text-left px-6 py-3 label-overline">URL</th>}
                  {showCount && <th className="text-right px-6 py-3 label-overline">Opens</th>}
                  {showBounceReason && <th className="text-left px-6 py-3 label-overline">Bounce reason</th>}
                  <th className="text-left px-6 py-3 label-overline">When</th>
                  <th className="text-left px-6 py-3 label-overline">Variant</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.contact_id}-${i}`} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <td className="px-6 py-3 text-sm font-medium">{r.email}</td>
                    <td className="px-6 py-3 text-sm text-neutral-600 dark:text-neutral-400">
                      {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-6 py-3 text-xs text-neutral-500 dark:text-neutral-400">{(r.tags || []).join(", ") || "—"}</td>
                    {showUrl && (
                      <td className="px-6 py-3 text-xs text-neutral-600 dark:text-neutral-400 max-w-xs truncate" title={r.url}>
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                          {r.url}
                        </a>
                      </td>
                    )}
                    {showCount && <td className="px-6 py-3 text-right text-sm">{r.count ?? 1}</td>}
                    {showBounceReason && (
                      <td className="px-6 py-3 text-xs text-neutral-600 dark:text-neutral-400 max-w-md" title={r.bounce_reason}>
                        {r.bounce_reason || "—"}
                      </td>
                    )}
                    <td className="px-6 py-3 text-sm text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                      {r.at ? new Date(r.at).toLocaleString() : "—"}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-500 dark:text-neutral-400">{r.variant || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPagination
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
